import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "@emisgroup/acp-application-intl";
import { IRootState } from "../../../../reducer/CombineReducer";
import { OutputConfigPicker } from "../OutputConfigPicker/OutputConfigPicker.logic";
import { CreateScheduleSlice } from "../CreateSchedule/redux";
import { getFormattedDate, GetValidDateFormat } from "../Helper/Script";
import {
  Input,
  Form,
  FormSection,
  FormElement,
  Button
} from "../../../Common/UI/Index";
import { ButtonVariantType, InputType } from "../../../Common/UI/Types";
import { ToggleButton } from "../ToggleButton/ToggleButton";
import { ReccurrenceEditItem } from "./Types";
import Picker from "~icons/ic/outline-apps";
import "./Style.scss";

/**
 * Responsible for rendering edit recurrence form
 */
export const EditReccurrenceFormView: React.FC = () => {
  const dispatch = useDispatch();
  const scheduling = useSelector((state: IRootState) => state.scheduling);
  const { schedulingRecurrenceItem, schedulingId } = scheduling;
  const { t: translate } = useTranslation();
  const [recurrenceData, setRecurrenceData] = useState({} as ReccurrenceEditItem);
  const [showPicker, setShowPicker] = useState(false);
  const [isRangeEndInvalid, setIsRangeEndInvalid] = useState(true);

  useEffect(() => {
    setRecurrenceData(schedulingRecurrenceItem);
  }, []);

  useEffect(() => {
    setIsRangeEndInvalid(
      recurrenceData.reportingPeriodStartDate >
      recurrenceData.reportingPeriodEndDate
    );
    const isSaveValid = recurrenceData &&
      recurrenceData.extractScheduleInstanceId &&
      recurrenceData.outputConfigId &&
      recurrenceData.outputConfigName &&
      recurrenceData.relativeRunDate &&
      recurrenceData.runDate &&
      recurrenceData.reportingPeriodStartDate &&
      recurrenceData.reportingPeriodEndDate &&
      recurrenceData.surveyName &&
      !isRangeEndInvalid;

    dispatch(CreateScheduleSlice.actions.setSchedulingRecurrenceItem({
      ...recurrenceData,
      ["runDate"]: GetValidDateFormat(new Date(recurrenceData.runDate)),
      ["relativeRunDate"]: GetValidDateFormat(new Date(recurrenceData.relativeRunDate)),
      ["reportingPeriodStartDate"]: GetValidDateFormat(new Date(recurrenceData.reportingPeriodStartDate)),
      ["reportingPeriodEndDate"]: GetValidDateFormat(new Date(recurrenceData.reportingPeriodEndDate))
    }));
    dispatch(CreateScheduleSlice.actions.enableSchedulingPopupSave(!!isSaveValid));
  }, [recurrenceData, isRangeEndInvalid]);

  /**
   * Action when value is changed
   * @param e React SyntheticEvent object
   */
  const onChangeValue = (e: React.SyntheticEvent<any, Event>): void => {
    const target = e.currentTarget;
    const name = target.name;
    const value = target.value;
    const newData = { ...recurrenceData, [name]: value };
    setRecurrenceData(newData);
  };

  /**
   * Action on changing date
   * @param e SyntheticEvent
   */
  const onChangeDate = (e: React.SyntheticEvent<any, Event>): void => {
    const target = e.currentTarget;
    const name = target.name;
    const value = getFormattedDate(new Date(target.value), false, true);
    if (value.length === 10) {
      const newData = { ...recurrenceData, [name]: value };
      setRecurrenceData(newData);
    }
  };

  /**
   * Action on pick output config
   * @param id output config id
   * @param name output config name
   */
  const onPickOutputConfig = (id: string, name: string): void => {
    const newData = { ...recurrenceData, ["outputConfigId"]: id, ["outputConfigName"]: name };
    setRecurrenceData(newData);
  };

  /**
   * To show or hide output config picker
   */
  const onTogglePicker = (): void => {
    setShowPicker(!showPicker);
  };

  /**
   * To handle while toggling the switch
   * @param selectedValue on/off
   */
  const handleOnToggle = (selectedValue: boolean): void => {
    setRecurrenceData({
      ...recurrenceData,
      isActive: selectedValue
    });
  };

  return (
    <>
      <Form>
        <FormSection
          data-testid="scheduling-recurrence-form">
          <FormElement
            data-testid="scheduling-recurrence-extractScheduleInstanceId-formElement"
            fieldId="extractScheduleInstanceId"
            labelText={translate("Scheduling.EditReccurrenceForm.extractScheduleInstanceId")}
            mandatory={true}
          >
            <Input
              data-testid="scheduling-recurrence-extractScheduleInstanceId"
              id="extractScheduleInstanceId"
              type={InputType.Text}
              name="extractScheduleInstanceId"
              value={schedulingId}
              disabled
            />
          </FormElement>
          <FormElement
            data-testid="scheduling-recurrence-runDate-formElement"
            fieldId="runDate"
            labelText={translate("Scheduling.EditReccurrenceForm.runDate")}
            mandatory={true}
          >
            <Input
              data-testid="scheduling-recurrence-runDate"
              id="runDate"
              type={InputType.Date}
              name="runDate"
              value={recurrenceData.runDate}
              onChange={onChangeDate}
            />
          </FormElement>
          <FormElement
            data-testid="scheduling-recurrence-relativeRunDate-formElement"
            fieldId="relativeRunDate"
            labelText={translate("Scheduling.EditReccurrenceForm.relativeRunDate")}
            mandatory={true}
          >
            <Input
              data-testid="scheduling-recurrence-relativeRunDate"
              id="relativeRunDate"
              type={InputType.Date}
              name="relativeRunDate"
              value={recurrenceData.relativeRunDate}
              onChange={onChangeDate}
            />
          </FormElement>
          <FormElement
            data-testid="scheduling-recurrence-reportingPeriodStartDate-formElement"
            fieldId="reportingPeriodStartDate"
            labelText={translate("Scheduling.EditReccurrenceForm.reportingPeriodStartDate")}
            mandatory={true}
          >
            <Input
              data-testid="scheduling-recurrence-reportingPeriodStartDate"
              id="reportingPeriodStartDate"
              type={InputType.Date}
              name="reportingPeriodStartDate"
              value={recurrenceData.reportingPeriodStartDate}
              onChange={onChangeDate}
            />
          </FormElement>
          <FormElement
            data-testid="scheduling-recurrence-reportingPeriodEndDate-formElement"
            fieldId="reportingPeriodEndDate"
            labelText={translate("Scheduling.EditReccurrenceForm.reportingPeriodEndDate")}
            mandatory={true}
            errorText={isRangeEndInvalid ? "End date should be greater than or equal to start date" : ""}
          >
            <Input
              data-testid="scheduling-recurrence-reportingPeriodEndDate"
              id="reportingPeriodEndDate"
              type={InputType.Date}
              name="reportingPeriodEndDate"
              value={recurrenceData.reportingPeriodEndDate}
              onChange={onChangeDate}
              invalid={isRangeEndInvalid}
            />
          </FormElement>
          <FormElement
            data-testid="scheduling-recurrence-surveyName-formElement"
            fieldId="surveyName"
            labelText={translate("Scheduling.EditReccurrenceForm.surveyName")}
            mandatory={true}
          >
            <Input
              data-testid="scheduling-recurrence-surveyName"
              id="surveyName"
              type={InputType.Text}
              name="surveyName"
              value={recurrenceData.surveyName}
              onChange={onChangeValue}
            />
          </FormElement>
        </FormSection>

        <FormSection
          data-testid="scheduling-recurrence-outputConfigId-formsection"
          horizontal={true}
          className="ebt-recurrence-form"
        >

          <FormElement
            data-testid="scheduling-recurrence-outputConfigName"
            fieldId="outputConfigName"
            labelText={translate("Scheduling.EditReccurrenceForm.outputConfigName")}
            mandatory={true}
          >
            <div className="ebt-switch-output">
              <Input
                id="outputConfigName"
                data-testid="scheduling-recurrence-outputConfigName"
                type={InputType.Text}
                name="outputConfigName"
                value={recurrenceData.outputConfigName}
                disabled={true}
                className="ebt-switch-input"
              />
              <Button
                variant={ButtonVariantType.FILLED}
                onClick={onTogglePicker}
                className="ebt-switch-picker"
              >
                <Picker title="picker" />
              </Button>
            </div>
          </FormElement>
          <div className="ebt-recurrence-form-toggle">
            <ToggleButton
              isSelected={recurrenceData.isActive}
              onToggle={handleOnToggle}
            />
          </div>
        </FormSection>
      </Form>
      {
        showPicker &&
        <OutputConfigPicker onToggle={onTogglePicker} onPick={onPickOutputConfig} />
      }
    </>);
};
